import type { VideoStatus } from "@sp/shared/enums";
import type { Video } from "./api/types";
import type { Tone } from "./stream-status";

export const VIDEO_TONE: Record<VideoStatus, Tone> = {
  uploading: "info",
  uploaded: "info",
  processing: "warn",
  ready: "ok",
  failed: "bad",
};

export const isSettling = (s: VideoStatus) => s === "uploaded" || s === "processing";

const busy = (v: Video) => v.variants.filter((x) => x.status === "pending" || x.status === "processing");

/** Videos the server is still working on, including extra qualities after the first is ready; the library polls while any exist. */
export const isWorking = (v: Video) => isSettling(v.status) || (v.status === "ready" && busy(v).length > 0);

/**
 * How far processing has got, for the library's progress bar. Before the first quality is ready that is the furthest
 * variant (the video becomes usable when it finishes); `null` while no server has picked the video up yet.
 */
export function processingPercent(v: Video): number | null {
  if (!isSettling(v.status)) return null;
  const running = v.variants.filter((x) => x.status === "processing" && x.progress !== null);
  return running.length ? Math.max(...running.map((x) => x.progress!)) : null;
}

/** Qualities still being made after the video is ready, such as "4k-30 · 40%" or "4k-30 · queued". */
export function pendingQualities(v: Video): string[] {
  if (v.status !== "ready") return [];
  return busy(v).map((x) => `${x.quality}-${x.fps} · ${x.status === "processing" && x.progress !== null ? `${x.progress}%` : "queued"}`);
}
